import { Metadata } from "next";

export const metadata: Metadata = {
  title: "Get Started - PentAGI",
  description:
    "Get started with PentAGI - an innovative tool for automated security testing",
};

export default function GetStartedPage() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-4xl font-bold mb-8">Getting Started with PentAGI</h1>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4">System Requirements</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Docker and Docker Compose</li>
          <li>Minimum 4GB RAM</li>
          <li>10GB free disk space</li>
          <li>Internet access for downloading images and updates</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4">License Agreement</h2>
        <p className="mb-4">
          Before using PentAGI, please read and agree to our End User License
          Agreement (EULA). The EULA can be found in our GitHub repository:{" "}
          <a
            href="https://github.com/vxcontrol/pentagi/blob/master/EULA.md"
            className="text-blue-500 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            EULA.md
          </a>
        </p>
        <p className="text-sm text-muted-foreground">
          For the most up-to-date information and current state of the project,
          please visit our{" "}
          <a
            href="https://github.com/vxcontrol/pentagi"
            className="text-blue-500 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            GitHub repository
          </a>
          .
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4">Quick Installation</h2>
        <div className="space-y-4">
          <p>1. Create a working directory:</p>
          <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto">
            mkdir pentagi && cd pentagi
          </pre>

          <p>2. Copy the configuration file:</p>
          <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto">
            curl -o .env
            https://raw.githubusercontent.com/vxcontrol/pentagi/master/.env.example
          </pre>

          <p>3. Fill in the required API keys in the .env file:</p>
          <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto">
            {`# Required: At least one of these LLM providers
OPEN_AI_KEY=your_openai_key
ANTHROPIC_API_KEY=your_anthropic_key

# Optional: Additional search capabilities
GOOGLE_API_KEY=your_google_key
GOOGLE_CX_KEY=your_google_cx
TAVILY_API_KEY=your_tavily_key
TRAVERSAAL_API_KEY=your_traversaal_key`}
          </pre>

          <p>4. Launch PentAGI:</p>
          <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto">
            {`curl -O https://raw.githubusercontent.com/vxcontrol/pentagi/master/docker-compose.yml
docker compose up -d`}
          </pre>

          <p className="mt-4">
            Visit{" "}
            <a
              href="https://localhost:8443"
              className="text-blue-500 hover:underline"
            >
              localhost:8443
            </a>{" "}
            to access PentAGI Web UI (default: admin@pentagi.com / admin)
          </p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4">System Architecture</h2>
        <div className="space-y-4">
          <p>
            PentAGI is built on a modular, scalable, and secure architecture.
            Key components:
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">1. Core Services</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Frontend UI: React-based web interface with TypeScript</li>
                <li>Backend API: REST and GraphQL APIs in Go</li>
                <li>
                  Vector Store: PostgreSQL with pgvector for semantic search
                </li>
                <li>Task Queue: Async task processing system</li>
                <li>AI Agent: Multi-agent system with specialized roles</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-2">2. Monitoring</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>OpenTelemetry: Unified observability data collection</li>
                <li>
                  Grafana: Real-time visualization and alerting dashboards
                </li>
                <li>VictoriaMetrics: High-performance time-series storage</li>
                <li>Jaeger: End-to-end distributed tracing</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-2">3. Analytics</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Langfuse: Advanced LLM observability and analytics</li>
                <li>ClickHouse: Column-oriented analytics data warehouse</li>
                <li>Redis: High-speed caching and rate limiting</li>
                <li>MinIO: S3-compatible object storage</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4">
          External System Integration
        </h2>

        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-2">Langfuse</h3>
            <p className="mb-2">
              For monitoring and analyzing AI agent operations:
            </p>
            <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto">
              {`# Add to your .env file:
LANGFUSE_BASE_URL=http://langfuse-web:3000

# Launch Langfuse stack:
curl -O https://raw.githubusercontent.com/vxcontrol/pentagi/master/docker-compose-langfuse.yml
docker compose -f docker-compose.yml -f docker-compose-langfuse.yml up -d`}
            </pre>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-2">
              Monitoring and Observability
            </h3>
            <p className="mb-2">For detailed system operation tracking:</p>
            <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto">
              {`# Add to your .env file:
OTEL_HOST=otelcol:8148

# Launch monitoring stack:
curl -O https://raw.githubusercontent.com/vxcontrol/pentagi/master/docker-compose-observability.yml
docker compose -f docker-compose.yml -f docker-compose-observability.yml up -d`}
            </pre>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-2">OAuth Integration</h3>
            <p>For GitHub and Google authentication, add to your .env file:</p>
            <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto">
              {`# GitHub OAuth
GITHUB_CLIENT_ID=your_github_client_id
GITHUB_CLIENT_SECRET=your_github_client_secret

# Google OAuth
GOOGLE_CLIENT_ID=your_google_client_id
GOOGLE_CLIENT_SECRET=your_google_client_secret`}
            </pre>
          </div>
        </div>
      </section>
    </div>
  );
}
